import React, { useState, useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────────────────────
   Flapaccessories.js — Home.js design system
   --navy #06112b · --blue #1c73ba · --gold #f5a623 · Clash Display + DM Sans
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

:root {
  --navy:#06112b;--navy2:#0d1f45;--navy3:#112359;
  --blue:#1c73ba;--blue2:#2b8de0;--blue3:#5aaef7;
  --gold:#f5a623;--gold2:#ffc94d;
  --muted:#94a3b8;--border:rgba(28,115,186,0.15);
}
.acc-page {
  min-height:100vh;
  background:linear-gradient(145deg,var(--navy) 0%,var(--navy3) 60%,#1a3570 100%);
  font-family:'DM Sans',sans-serif;color:#fff;overflow-x:hidden;
}
.acc-page::before {
  content:'';position:fixed;inset:0;
  background-image:linear-gradient(rgba(28,115,186,0.05) 1px,transparent 1px),
    linear-gradient(90deg,rgba(28,115,186,0.05) 1px,transparent 1px);
  background-size:52px 52px;pointer-events:none;z-index:0;
}
.acc-nav {
  position:sticky;top:0;z-index:50;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 5%;height:68px;
  background:rgba(6,17,43,0.9);backdrop-filter:blur(20px);
  border-bottom:1px solid var(--border);
}
.acc-logo { font-family:'Clash Display',sans-serif;font-size:1.5rem;font-weight:700;color:#fff;text-decoration:none; }
.acc-logo span{color:var(--gold);}
.acc-back {
  background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.12);
  border-radius:8px;padding:7px 14px;color:rgba(255,255,255,0.75);
  font-size:0.85rem;cursor:pointer;text-decoration:none;transition:background 0.2s;
}
.acc-back:hover{background:rgba(255,255,255,0.12);color:#fff;}

/* Hero */
.acc-hero {
  padding:72px 5% 40px;position:relative;z-index:1;
  max-width:1200px;margin:0 auto;
  display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center;
}
@media(max-width:860px){.acc-hero{grid-template-columns:1fr;gap:32px;}}
.acc-eyebrow {
  display:inline-flex;align-items:center;gap:8px;
  background:rgba(245,166,35,0.12);border:1px solid rgba(245,166,35,0.3);
  border-radius:99px;padding:5px 14px;font-size:0.72rem;font-weight:700;
  color:var(--gold);letter-spacing:2px;text-transform:uppercase;margin-bottom:20px;
}
.acc-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:var(--gold);animation:accPulse 2s infinite;}
@keyframes accPulse{0%,100%{box-shadow:0 0 0 0 rgba(245,166,35,0.4);}50%{box-shadow:0 0 0 5px rgba(245,166,35,0);}}
.acc-h1 {
  font-family:'Clash Display',sans-serif;
  font-size:clamp(2rem,3.5vw + 0.5rem,3.2rem);
  font-weight:700;line-height:1.1;margin-bottom:16px;
}
.acc-h1 em{font-style:normal;color:var(--blue3);}
.acc-sub{font-size:1rem;color:rgba(255,255,255,0.6);line-height:1.7;max-width:460px;margin-bottom:28px;}

/* Variant tabs */
.acc-variant-tabs{display:flex;gap:10px;margin-bottom:20px;flex-wrap:wrap;}
.acc-tab{
  padding:9px 20px;border-radius:99px;border:1px solid var(--border);
  background:transparent;color:rgba(255,255,255,0.6);
  font-family:'DM Sans',sans-serif;font-size:0.85rem;font-weight:600;
  cursor:pointer;transition:all 0.2s;
}
.acc-tab.active{background:var(--blue);border-color:var(--blue);color:#fff;}
.acc-tab:hover:not(.active){border-color:rgba(28,115,186,0.4);color:#fff;}

/* Carousel */
.acc-carousel-wrap{position:relative;}
.acc-carousel {
  position:relative;border-radius:22px;overflow:hidden;
  background:rgba(13,31,69,0.6);border:1px solid rgba(28,115,186,0.2);
  aspect-ratio:4/3;
  box-shadow:0 20px 60px rgba(0,0,0,0.4);
}
.acc-carousel-img {
  width:100%;height:100%;object-fit:contain;
  padding:24px;
  transition:opacity 0.4s,transform 0.4s;
}
.acc-carousel-nav {
  position:absolute;top:50%;transform:translateY(-50%);
  width:36px;height:36px;border-radius:50%;
  background:rgba(6,17,43,0.8);border:1px solid var(--border);
  color:rgba(255,255,255,0.8);font-size:18px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:background 0.2s;z-index:5;
}
.acc-carousel-nav:hover{background:rgba(28,115,186,0.3);}
.acc-carousel-nav.prev{left:10px;}
.acc-carousel-nav.next{right:10px;}
.acc-carousel-dots{display:flex;justify-content:center;gap:6px;margin-top:12px;}
.acc-carousel-dot{
  width:6px;height:6px;border-radius:50%;
  background:rgba(255,255,255,0.2);border:none;cursor:pointer;transition:background 0.2s,width 0.2s;
}
.acc-carousel-dot.active{background:var(--blue3);width:18px;border-radius:99px;}

/* Thumbnail row */
.acc-thumbs{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap;}
.acc-thumb{
  width:64px;height:64px;border-radius:10px;overflow:hidden;
  border:2px solid transparent;cursor:pointer;transition:border-color 0.2s;
  background:rgba(13,31,69,0.5);flex-shrink:0;
}
.acc-thumb img{width:100%;height:100%;object-fit:contain;padding:4px;}
.acc-thumb.active{border-color:var(--blue3);}

/* Order panel */
.acc-order {
  background:rgba(255,255,255,0.03);border:1px solid rgba(28,115,186,0.2);
  border-radius:20px;padding:28px;position:relative;overflow:hidden;
  backdrop-filter:blur(16px);
}
.acc-order::before{
  content:'';position:absolute;top:-30%;right:-30%;width:60%;height:60%;
  background:radial-gradient(circle,rgba(245,166,35,0.06) 0%,transparent 70%);pointer-events:none;
}
.acc-section-label{display:block;font-size:0.72rem;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--blue3);margin-bottom:12px;}
.acc-section-heading{font-family:'Clash Display',sans-serif;font-size:clamp(1.5rem,2.5vw,2.2rem);font-weight:700;color:#fff;line-height:1.15;margin-bottom:8px;}
.acc-section-heading em{font-style:normal;color:var(--gold);}
.acc-price{font-family:'Clash Display',sans-serif;font-size:2.2rem;font-weight:700;color:var(--gold);line-height:1;margin-bottom:4px;}
.acc-price-sub{font-size:0.8rem;color:var(--muted);margin-bottom:20px;}

/* Material selector */
.acc-materials{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px;}
.acc-mat-btn{
  padding:8px 18px;border-radius:99px;border:1px solid var(--border);
  background:transparent;color:rgba(255,255,255,0.6);
  font-family:'DM Sans',sans-serif;font-size:0.82rem;font-weight:600;
  cursor:pointer;transition:all 0.2s;
}
.acc-mat-btn.active{background:rgba(28,115,186,0.2);border-color:var(--blue);color:#fff;}
.acc-mat-btn:hover:not(.active){border-color:rgba(28,115,186,0.35);color:#fff;}

/* Stepper + buttons */
.acc-stepper{display:flex;align-items:center;gap:0;background:rgba(255,255,255,0.05);border:1px solid var(--border);border-radius:10px;overflow:hidden;margin-bottom:20px;width:fit-content;}
.acc-stepper-btn{width:42px;height:42px;background:transparent;border:none;color:var(--blue3);font-size:1.2rem;cursor:pointer;transition:background 0.2s;}
.acc-stepper-btn:hover{background:rgba(28,115,186,0.15);}
.acc-stepper-val{width:52px;text-align:center;background:transparent;border:none;border-left:1px solid var(--border);border-right:1px solid var(--border);color:#fff;font-size:0.95rem;font-weight:600;height:42px;font-family:'DM Sans',sans-serif;}
.acc-btn-gold{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:13px 24px;background:linear-gradient(135deg,var(--gold),var(--gold2));color:var(--navy);border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:0.95rem;font-weight:700;cursor:pointer;transition:transform 0.2s,box-shadow 0.2s;box-shadow:0 6px 24px rgba(245,166,35,0.3);margin-bottom:10px;}
.acc-btn-gold:hover{transform:translateY(-2px);box-shadow:0 10px 32px rgba(245,166,35,0.4);}
.acc-btn-secondary{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:12px 24px;background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.14);border-radius:10px;font-family:'DM Sans',sans-serif;font-size:0.9rem;font-weight:600;cursor:pointer;transition:background 0.2s,transform 0.2s;}
.acc-btn-secondary:hover{background:rgba(255,255,255,0.12);transform:translateY(-2px);}

/* Main body grid */
.acc-body{padding:48px 5% 80px;position:relative;z-index:1;max-width:1200px;margin:0 auto;}
.acc-grid{display:grid;grid-template-columns:1fr 380px;gap:40px;align-items:start;}
@media(max-width:860px){.acc-grid{grid-template-columns:1fr;}}

/* Features */
.acc-features-section{padding:72px 5%;background:rgba(6,17,43,0.5);border-top:1px solid var(--border);border-bottom:1px solid var(--border);position:relative;z-index:1;}
.acc-features-inner{max-width:1100px;margin:0 auto;}
.acc-features-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:18px;margin-top:36px;}
.acc-feat-card{background:rgba(255,255,255,0.03);border:1px solid var(--border);border-radius:18px;padding:22px;transition:border-color 0.3s,transform 0.3s;position:relative;overflow:hidden;}
.acc-feat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--blue),var(--blue3));transform:scaleX(0);transform-origin:left;transition:transform 0.3s;}
.acc-feat-card:hover{border-color:rgba(28,115,186,0.35);transform:translateY(-4px);}
.acc-feat-card:hover::before{transform:scaleX(1);}
.acc-feat-icon{font-size:26px;margin-bottom:10px;display:block;}
.acc-feat-title{font-family:'Clash Display',sans-serif;font-size:0.95rem;font-weight:600;color:#fff;margin-bottom:5px;}
.acc-feat-desc{font-size:0.82rem;color:rgba(255,255,255,0.5);line-height:1.6;}

/* Demo modal */
.acc-modal-overlay{position:fixed;inset:0;z-index:200;background:rgba(6,17,43,0.88);backdrop-filter:blur(12px);display:flex;align-items:center;justify-content:center;padding:24px;}
.acc-modal{background:linear-gradient(145deg,var(--navy2),var(--navy3));border:1px solid rgba(28,115,186,0.3);border-radius:24px;padding:36px;max-width:480px;width:100%;box-shadow:0 24px 80px rgba(0,0,0,0.6);position:relative;}
.acc-modal-close{position:absolute;top:16px;right:16px;width:32px;height:32px;border-radius:50%;background:rgba(255,255,255,0.08);border:none;color:rgba(255,255,255,0.6);font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s;}
.acc-modal-close:hover{background:rgba(255,255,255,0.15);color:#fff;}
.acc-demo-scene{display:flex;align-items:center;justify-content:center;gap:28px;padding:20px 0;}
.acc-demo-acc{width:100px;height:100px;border-radius:14px;object-fit:contain;border:2px solid var(--border);}
.acc-demo-phone{width:130px;height:250px;background:#0a0a0a;border-radius:20px;border:2px solid #333;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 16px 40px rgba(0,0,0,0.6);}
.acc-demo-bar{height:26px;background:#111;display:flex;align-items:center;justify-content:center;}
.acc-demo-notch{width:44px;height:7px;background:#222;border-radius:99px;}
.acc-demo-screen{flex:1;background:#0d1f45;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px;gap:6px;}
.acc-demo-msg{font-size:9px;color:var(--blue3);text-align:center;font-weight:600;animation:accFadePulse 1s infinite;}
@keyframes accFadePulse{0%,100%{opacity:1}50%{opacity:0.4}}
.acc-demo-profile{display:flex;flex-direction:column;align-items:center;gap:4px;width:100%;}
.acc-demo-avatar{width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,var(--blue),var(--blue3));display:flex;align-items:center;justify-content:center;font-size:15px;color:#fff;font-weight:700;}
.acc-demo-name{font-size:9px;color:#fff;font-weight:700;}
.acc-demo-role{font-size:7px;color:var(--muted);}
.acc-demo-chips{display:flex;gap:4px;flex-wrap:wrap;justify-content:center;}
.acc-demo-chip{font-size:6px;padding:2px 5px;border-radius:99px;background:rgba(28,115,186,0.2);color:var(--blue3);border:1px solid rgba(28,115,186,0.3);}

.acc-toast{position:fixed;bottom:28px;left:50%;transform:translateX(-50%);background:rgba(13,31,69,0.97);color:#fff;border:1px solid var(--border);border-radius:10px;padding:12px 22px;font-size:0.85rem;font-weight:600;z-index:300;box-shadow:0 8px 32px rgba(0,0,0,0.4);animation:toastUp 0.3s ease;}
@keyframes toastUp{from{transform:translateX(-50%) translateY(10px);opacity:0;}}
`;

const PRODUCTS = {
  keyring: {
    name: 'Flap Keyring', emoji: '🔑',
    materials: [
      { key: 'acrylic', label: 'Acrylic', price: 599, imgs: ['/images/keyringacrylic_front.png', '/images/keyringacrylic_back.png'] },
      { key: 'wood',    label: 'Wood',    price: 999, imgs: ['/images/keyringwood_front.png',    '/images/keyringwood_back.png'   ] },
    ],
    features: [
      { icon:'🔑', title:'Compact Design',   desc:'Attaches to any keychain without bulk — always within reach.' },
      { icon:'📲', title:'Instant Tap',      desc:'NFC chip inside shares your full profile in a single tap.' },
      { icon:'🌿', title:'Eco Materials',    desc:'Choose acrylic for precision or wood for a natural, premium feel.' },
      { icon:'🎨', title:'Custom Print',     desc:'Your logo or QR printed on both sides, sharp and durable.' },
    ],
  },
  ring: {
    name: 'Flap Ring', emoji: '💍',
    materials: [
      { key: 'silver',   label: 'Silver',   price: 5599,   imgs: ['/images/ring_silver.png']   },
      { key: 'gold',     label: 'Gold',     price: 55999,  imgs: ['/images/ring_gold.png']     },
      { key: 'platinum', label: 'Platinum', price: 101999, imgs: ['/images/ring_platinum.png'] },
    ],
    features: [
      { icon:'💍', title:'Wearable NFC',    desc:'Built-in NFC chip lets you share your profile by pointing your ring at a phone.' },
      { icon:'✨', title:'Precious Metals', desc:'Available in 925 silver, 18K gold, and platinum — genuine fine jewellery.' },
      { icon:'🔒', title:'Always Ready',    desc:'No battery, no charging — works 24/7 from the energy of your phones NFC field.' },
      { icon:'🎁', title:'Gift Ready',      desc:'Ships in premium packaging — a perfect networking gift for any occasion.' },
    ],
  },
};

function fmt(n) { return `Rs. ${n.toLocaleString('en-IN')}`; }

export default function Flapaccessories() {
  const [variant,  setVariant]  = useState('keyring');
  const [matKey,   setMatKey]   = useState('acrylic');
  const [imgIdx,   setImgIdx]   = useState(0);
  const [qty,      setQty]      = useState(1);
  const [toast,    setToast]    = useState('');
  const [demo,     setDemo]     = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const timerRef = useRef(null);

  const prod = PRODUCTS[variant];
  const mat  = prod.materials.find(m => m.key === matKey) || prod.materials[0];
  const imgs = mat.imgs;

  // Auto-cycle images
  useEffect(() => {
    clearInterval(timerRef.current);
    setImgIdx(0);
    if (imgs.length > 1) timerRef.current = setInterval(() => setImgIdx(i => (i+1) % imgs.length), 4000);
    return () => clearInterval(timerRef.current);
  }, [variant, matKey, imgs.length]);

  // Reset material when switching variant
  useEffect(() => { setMatKey(prod.materials[0].key); }, [variant]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2800); };

  const handleOrder = () => {
    showToast('Opening WhatsApp…');
    const msg = `Hi! I'd like to order ${qty} ${prod.name} (${mat.label}) for ${fmt(mat.price)} each. Total: ${fmt(qty * mat.price)}.`;
    setTimeout(() => window.open(`https://wa.me/9779802365432?text=${encodeURIComponent(msg)}`, '_blank'), 900);
  };

  const handleDemo = () => {
    setDemo(true); setDemoStep(1);
    setTimeout(() => setDemoStep(2), 800);
    setTimeout(() => setDemoStep(3), 1600);
  };

  return (
    <div className="acc-page">
      <style>{STYLES}</style>

      {/* Nav */}
      <nav className="acc-nav">
        <a href="/" className="acc-logo">flap<span>.</span></a>
        <a href="/" className="acc-back">← Back to home</a>
      </nav>

      {/* Variant selector + Hero */}
      <div style={{ padding:'48px 5% 0', maxWidth:1200, margin:'0 auto', position:'relative', zIndex:1 }}>
        <div className="acc-eyebrow" style={{ marginBottom:20 }}>
          <span className="acc-eyebrow-dot"/>NFC Accessories
        </div>
        <h1 className="acc-h1" style={{ marginBottom:24 }}>Flap <em>Accessories</em></h1>

        {/* Variant tabs */}
        <div className="acc-variant-tabs">
          {Object.entries(PRODUCTS).map(([k,p]) => (
            <button key={k} className={`acc-tab ${variant===k?'active':''}`}
              onClick={() => setVariant(k)}>
              {p.emoji} {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Body grid */}
      <div className="acc-body">
        <div className="acc-grid">
          {/* Left: carousel + thumbs */}
          <div>
            <div className="acc-carousel-wrap">
              <div className="acc-carousel">
                <img src={imgs[imgIdx]} alt={prod.name} className="acc-carousel-img"/>
                {imgs.length > 1 && <>
                  <button className="acc-carousel-nav prev" onClick={()=>setImgIdx(i=>(i-1+imgs.length)%imgs.length)}>‹</button>
                  <button className="acc-carousel-nav next" onClick={()=>setImgIdx(i=>(i+1)%imgs.length)}>›</button>
                </>}
              </div>
              {imgs.length > 1 && (
                <div className="acc-carousel-dots">
                  {imgs.map((_,i) => <button key={i} className={`acc-carousel-dot ${i===imgIdx?'active':''}`} onClick={()=>setImgIdx(i)}/>)}
                </div>
              )}
              {/* Thumb row — all material images */}
              <div className="acc-thumbs">
                {prod.materials.flatMap(m => m.imgs.map((img,j) => (
                  <div key={`${m.key}-${j}`}
                    className={`acc-thumb ${img===imgs[imgIdx]?'active':''}`}
                    onClick={()=>{ setMatKey(m.key); setImgIdx(j); }}>
                    <img src={img} alt={m.label}/>
                  </div>
                )))}
              </div>
            </div>
          </div>

          {/* Right: order panel */}
          <div className="acc-order">
            <span className="acc-section-label">Order</span>
            <h2 className="acc-section-heading">{prod.name} — <em>{mat.label}</em></h2>
            <div className="acc-price">{fmt(mat.price)}</div>
            <div className="acc-price-sub">per unit · free delivery in Kathmandu</div>

            {/* Material */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>
                Material
              </div>
              <div className="acc-materials">
                {prod.materials.map(m => (
                  <button key={m.key} className={`acc-mat-btn ${matKey===m.key?'active':''}`}
                    onClick={()=>{ setMatKey(m.key); setImgIdx(0); }}>
                    {m.label} — {fmt(m.price)}
                  </button>
                ))}
              </div>
            </div>

            {/* Qty */}
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:8 }}>
              Quantity
            </div>
            <div className="acc-stepper">
              <button className="acc-stepper-btn" onClick={()=>setQty(q=>Math.max(1,q-1))}>−</button>
              <input className="acc-stepper-val" type="number" value={qty} min="1"
                onChange={e=>setQty(Math.max(1,+e.target.value))}/>
              <button className="acc-stepper-btn" onClick={()=>setQty(q=>q+1)}>+</button>
            </div>
            <div style={{ fontSize:'0.8rem', color:'var(--muted)', marginBottom:20 }}>
              Total: <strong style={{color:'var(--gold)'}}>{fmt(qty * mat.price)}</strong>
            </div>

            <button className="acc-btn-gold" onClick={handleOrder}>📱 Order via WhatsApp</button>
            <button className="acc-btn-secondary" onClick={handleDemo}>▶ View Demo</button>

            {/* Includes */}
            <div style={{ marginTop:22, paddingTop:18, borderTop:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize:'0.72rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Includes</div>
              {['NFC chip pre-programmed','Flap dashboard activation','Profile link setup','1-year warranty'].map(i=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:7 }}>
                  <span style={{ color:'var(--gold)', fontSize:12 }}>✓</span>
                  <span style={{ fontSize:'0.82rem', color:'rgba(255,255,255,0.6)' }}>{i}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="acc-features-section">
        <div className="acc-features-inner">
          <span className="acc-section-label">Features</span>
          <h2 className="acc-section-heading">{prod.name} <em>highlights</em></h2>
          <div className="acc-features-grid">
            {prod.features.map(f => (
              <div className="acc-feat-card" key={f.title}>
                <span className="acc-feat-icon">{f.icon}</span>
                <div className="acc-feat-title">{f.title}</div>
                <div className="acc-feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo modal */}
      {demo && (
        <div className="acc-modal-overlay" onClick={e=>{ if(e.target===e.currentTarget){setDemo(false);setDemoStep(0);}}}>
          <div className="acc-modal">
            <button className="acc-modal-close" onClick={()=>{setDemo(false);setDemoStep(0);}}>✕</button>
            <h3 style={{ fontFamily:"'Clash Display',sans-serif", fontSize:'1.25rem', fontWeight:700, color:'#fff', marginBottom:6 }}>
              See it in action
            </h3>
            <p style={{ fontSize:'0.82rem', color:'var(--muted)', marginBottom:20 }}>
              Tap your {prod.name} against any NFC-enabled phone to share your profile instantly.
            </p>
            <div className="acc-demo-scene">
              <img src={imgs[0]} alt={prod.name} className="acc-demo-acc"
                style={{ transform: demoStep >= 2 ? 'translateX(16px) scale(0.95)' : 'none', transition:'transform 0.5s' }}/>
              <div style={{ fontSize:20, color:'var(--blue3)', animation: demoStep>=2 ? 'accFadePulse 0.8s 2' : 'none' }}>→</div>
              <div className="acc-demo-phone">
                <div className="acc-demo-bar"><div className="acc-demo-notch"/></div>
                <div className="acc-demo-screen">
                  {demoStep === 1 && <div className="acc-demo-msg">📡 Scanning for NFC…</div>}
                  {demoStep === 2 && <div className="acc-demo-msg">⚡ Card detected!</div>}
                  {demoStep >= 3 && (
                    <div className="acc-demo-profile">
                      <div className="acc-demo-avatar">F</div>
                      <div className="acc-demo-name">Flap Profile</div>
                      <div className="acc-demo-role">Your name here</div>
                      <div className="acc-demo-chips">
                        {['LinkedIn','Email','Phone'].map(l=><span key={l} className="acc-demo-chip">{l}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {demoStep >= 3
              ? <button className="acc-btn-gold" style={{ marginTop:16 }} onClick={handleOrder}>Order Now — {fmt(mat.price)}</button>
              : <button className="acc-btn-secondary" style={{ marginTop:16, border:'1px solid var(--border)' }} onClick={handleDemo}>▶ Restart</button>
            }
          </div>
        </div>
      )}

      {toast && <div className="acc-toast">{toast}</div>}
    </div>
  );
}